import { useQueryClient } from '@tanstack/react-query'
import {
  Button,
  Checkbox,
  Col,
  Flex,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select
} from 'antd'
import { ModalProps } from 'antd/lib'
import { TFunction } from 'i18next'
import { useEffect } from 'react'

import {
  useClassifierServiceGetRiskGroupClassifierElementEndpoint,
  useClassifierServiceGetRiskGroupClassifierElementListEndpoint,
  useClassifierServiceGetRiskGroupClassifierElementListEndpointKey,
  useClassifierServiceUpdateRiskGroupClassifierElementEndpoint,
  useClassifierServiceCreateRiskGroupClassifierElementEndpoint
} from 'src/api/queries'
import { ApiError } from 'src/api/requests'
import { LANGUAGE_KEYS, requiredRules } from 'src/app-constants'
import { EditableFormItem, message } from 'src/components'
import { useLocales } from 'src/locales'
import { formErrorHandler } from 'src/utils'

type Props = {
  id?: string
  onCancel: () => void
} & ModalProps

const getModalText = (hasId: boolean, t: TFunction) => {
  return {
    title: hasId ? t('risk_groups.edit_title') : t('risk_groups.add_title'),
    button: hasId ? t('common.edit') : t('common.add')
  }
}

export const RiskGroupModal = ({ id, onCancel, ...props }: Props) => {
  const { t, currentLang } = useLocales()

  const { button, title } = getModalText(!!id, t)

  const [form] = Form.useForm()

  const queryClient = useQueryClient()

  const { data } =
    useClassifierServiceGetRiskGroupClassifierElementListEndpoint()

  const { data: element } =
    useClassifierServiceGetRiskGroupClassifierElementEndpoint(
      { elementId: String(id) },
      [id],
      { enabled: !!id }
    )

  const { mutate: edit, isPending: isEditing } =
    useClassifierServiceUpdateRiskGroupClassifierElementEndpoint({
      onError: (error: ApiError) => formErrorHandler(error.body.errors),
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [
            useClassifierServiceGetRiskGroupClassifierElementListEndpointKey
          ]
        })
        message.success(t('risk_groups.success_edit'))
        onCancel()
      }
    })

  const { mutate: create, isPending: isCreating } =
    useClassifierServiceCreateRiskGroupClassifierElementEndpoint({
      onError: (error: ApiError) => formErrorHandler(error.body.errors),
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [
            useClassifierServiceGetRiskGroupClassifierElementListEndpointKey
          ]
        })
        message.success(t('risk_groups.success_create'))
        onCancel()
      }
    })

  const onSave = async () => {
    const values = await form.validateFields()

    id
      ? edit({ requestBody: values, elementId: id })
      : create({ requestBody: values })
  }

  const selectOptions = data?.data?.map((item) => ({
    value: item.id,
    label: item?.translation?.[currentLang?.value]
  }))

  useEffect(() => {
    if (id && element) form.setFieldsValue(element)
  }, [id, element])

  return (
    <Modal
      footer={null}
      width={800}
      title={title}
      maskClosable={false}
      open
      onCancel={onCancel}
      {...props}
    >
      <Form form={form} requiredMark={false} layout="vertical">
        <Row gutter={[24, 24]} align="bottom">
          {LANGUAGE_KEYS.map((langKey) => {
            return (
              <Col span={12} key={langKey}>
                <EditableFormItem
                  isEditing={true}
                  name={['translation', langKey]}
                  label={`${langKey.toUpperCase()}`}
                  rules={requiredRules}
                  className="full-width"
                >
                  <Input placeholder={t('common.enter_text')} />
                </EditableFormItem>
              </Col>
            )
          })}

          <Col span={24}>
            <Form.Item name="parentId" label={t('risk_groups.parent_category')}>
              <Select
                allowClear
                placeholder={t('risk_groups.parent_category')}
                options={selectOptions}
              />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item name="forKids" valuePropName="checked">
              <Checkbox>{t('risk_groups.for_kids')}</Checkbox>
            </Form.Item>

            {/*   <Form.Item
              name="recommendedPreventiveTreatment"
              valuePropName="checked"
            >
              <Checkbox>
                {t('risk_groups.preventive_treatment_recommended')}
              </Checkbox>
            </Form.Item> */}
          </Col>

          <Col span={24}>
            <Form.Item name="order" label={t('common.order')}>
              <InputNumber
                className="full-width"
                placeholder={t('common.order')}
              />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item name="comments" label={t('common.comments')}>
              <Input.TextArea rows={5} />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Flex gap={16} justify="center">
              <Button onClick={onCancel}>{t('common.cancel')}</Button>
              <Button
                type="primary"
                onClick={onSave}
                loading={isCreating || isEditing}
              >
                {button}
              </Button>
            </Flex>
          </Col>
        </Row>
      </Form>
    </Modal>
  )
}
